import styles from './ScenarioFour.module.scss';
import {
    selectFiltersAllValues,
    selectMarketValue,
    selectRmValue,
    selectScenarioFourOutlierData,
    selectTeamValue,
    selectTimeslicerEndMonth,
    selectTimeslicerStartMonth,
    selectZoneValue,
    selectRmDropdownValues,
    selectScenarioFourSelectedRM
} from 'selectors/selectors';
import { areAllFilterValuesNumberValues } from 'utils/filters';
import { generateRequestParamsForScenarioApi } from 'utils/api';
import { ChartType, Scenarios, ScenarioType } from 'types/types';
import ContentContainer from 'components/content-container/ContentContainer';
import ChartWrapper from 'components/chart-wrapper/ChartWrapper';
import InfiniteTable from 'components/infinite-table/InfiniteTable';
import ProductBreakdown from 'components/product-breakdown/ProductBreakdown';

import { detailedViewTable } from 'constants/scenarioFour';
import {
    fetchScenarioFourOutliers,
    setSelectedOutlier,
    setSelectedRM,
    fetchScenarioFourStats,
    resetAll
} from 'slices/ScenarioFourSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import {
    scenarioFourOutlierChartOptions,
    scenarioFourNoResultOutlierChartOptions
} from 'utils/charts';
import infoIcon from 'assets/images/info-icon.svg';
import darkInfoIcon from 'assets/images/dark-info-icon.svg';

const ScenarioFour = () => {
    const dispatch = useDispatch();

    const [iconIsHovered, setIconIsHovered] = useState(false);

    // Selectors
    const tsStart = useSelector(selectTimeslicerStartMonth) || {};
    const tsEnd = useSelector(selectTimeslicerEndMonth) || {};

    const zoneValue = useSelector(selectZoneValue);
    const marketValue = useSelector(selectMarketValue);
    const teamValue = useSelector(selectTeamValue);
    const rmValue = useSelector(selectRmValue);

    const outlierData = useSelector(selectScenarioFourOutlierData);
    const allValues = useSelector(selectFiltersAllValues);
    const rmList = useSelector(selectRmDropdownValues);
    const selectedStateRm = useSelector(selectScenarioFourSelectedRM);

    useEffect(() => {
        if (tsStart && tsEnd) {
            if (
                areAllFilterValuesNumberValues({
                    startMonth: tsStart.epoch,
                    endMonth: tsEnd.epoch,
                    zone: zoneValue,
                    market: marketValue,
                    team: teamValue,
                    rm: rmValue
                })
            ) {
                dispatch(
                    fetchScenarioFourStats(
                        generateRequestParamsForScenarioApi(
                            ScenarioType.Stats,
                            Scenarios.Four,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchScenarioFourOutliers(
                        generateRequestParamsForScenarioApi(
                            ChartType.Outliers,
                            Scenarios.Four,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch,
                            allValues
                        )
                    )
                );
                dispatch(resetAll());
            }
        }
    }, [marketValue, teamValue, rmValue, tsStart, tsEnd]);

    /**
     * This function will set outlier(rm) value into redux state
     * to used by product breakdown component.
     * @param {Object} event Outlier click event
     */
    const onOutlierClick = (event) => {
        if (event.name) {
            if (selectedStateRm === event.name) {
                dispatch(setSelectedRM(''));
            } else {
                dispatch(setSelectedRM(event.name));
            }

            const selectedRm = rmList.find((r) => r.label === event.name);
            const outlier = outlierData.outliers.find(
                (ol) => ol.rmEmployeeId === event.name
            );

            dispatch(
                setSelectedOutlier({
                    label: !selectedRm ? event.name : selectedRm.label,
                    score: outlier.score
                })
            );
        } else {
            throw Error('S4::OutlierChart::Unable to get outlier id');
        }
    };

    const memoizedOutlierChart = useMemo(() => {
        return (
            <ReactECharts
                option={
                    outlierData && outlierData.outliers.length > 0
                        ? scenarioFourOutlierChartOptions(
                              outlierData,
                              selectedStateRm
                          )
                        : scenarioFourNoResultOutlierChartOptions(outlierData)
                }
                autoResize={false}
                style={{ width: '100%', height: '100%' }}
                onEvents={{ click: onOutlierClick }}
            ></ReactECharts>
        );
    }, [outlierData, selectedStateRm]);

    return (
        <ContentContainer>
            <div className={styles.chartsContainer}>
                <ChartWrapper comboClass={styles.rightMargin} width={'30%'}>
                    {memoizedOutlierChart}
                </ChartWrapper>
                <div
                    className={
                        selectedStateRm &&
                        outlierData &&
                        outlierData.outliers.length > 0
                            ? styles.iconContainerWithRm
                            : styles.iconContainer
                    }
                >
                    <img
                        src={iconIsHovered ? darkInfoIcon : infoIcon}
                        alt={''}
                        onMouseOver={() => setIconIsHovered(true)}
                        onMouseOut={() => setIconIsHovered(false)}
                    />
                    <div
                        className={styles.container}
                        style={{
                            visibility: iconIsHovered ? 'visible' : 'hidden'
                        }}
                    >
                        <p>
                            RMs executing same product EO trades for multiple
                            clients within a month
                        </p>
                    </div>
                </div>

                <div className="grow" width={'70%'}>
                    <ProductBreakdown />
                </div>
            </div>
            <div className={styles.tableContainer}>
                <div className={styles.tableTitle}>Detailed View</div>
                <InfiniteTable
                    zone={zoneValue}
                    market={marketValue}
                    team={teamValue}
                    rm={rmValue}
                    start={tsStart}
                    end={tsEnd}
                    allValues={allValues}
                    columns={detailedViewTable}
                    scenario={Scenarios.Four}
                />
            </div>
        </ContentContainer>
    );
};

export default ScenarioFour;
